import { redactVisibleDocumentEvidencePaths } from "@repo/shared/document-evidence-link";
import { common, createLowlight } from "lowlight";
import { type CSSProperties, Fragment, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ClaudiaReferenceLink,
  getClaudiaReferenceInternalPath,
} from "@/components/ui/claudia-reference-link";
import {
  markdownTableCellClasses,
  markdownTableClasses,
  markdownTableHeadClasses,
  markdownTableHeaderCellClasses,
  markdownTableWrapperClasses,
} from "@/components/ui/markdown-table-classes";
import {
  rehypeDocumentEvidenceRuns,
  renderDocumentEvidenceLink,
} from "@/components/ui/message-markdown-evidence";
import { rehypeMessageHighlights } from "@/components/ui/message-markdown-highlight";
import { rehypeImagesAsLinks } from "@/components/ui/message-markdown-images";
import { renderMessageMentionLink } from "@/components/ui/message-markdown-mention";
import { normalizeReferenceLabel } from "@/components/ui/reference-chip";
import {
  type MessageFeatureFlags,
  resolveMessageFeatureFlags,
} from "@/features/rich-text/components/message-rich-text-config";
import {
  useOptionalMentionInteraction,
  useReferencePresentationResolver,
} from "@/features/rich-text/mentions";
import { classifyDocumentEvidenceHref } from "@/lib/document-evidence-link";
import { getInternalAppLinkLabel, toInternalAppPath } from "@/lib/internal-link-label";
import {
  canonicalizeMessageMentionAnnotations,
  parseMessageMentionHref,
} from "@/lib/message-mention-link";
import { flattenReactNodeText } from "@/lib/react-node-text";
import { cn } from "@/lib/utils";

interface MessageMarkdownProps {
  content: string;
  className?: string;
  onNavigate?: (path: string) => void;
  features?: Partial<MessageFeatureFlags>;
}

type HighlightedCodeNode =
  | {
      type: "text";
      value: string;
    }
  | {
      type: "element";
      tagName: string;
      properties: {
        className?: string[] | string;
      };
      children: HighlightedCodeNode[];
    };

function isExternalHref(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

const headingClasses = {
  h1: "mt-4 mb-2 text-base font-semibold leading-snug text-foreground first:mt-0",
  h2: "mt-3 mb-1.5 text-sm font-semibold leading-snug text-foreground first:mt-0",
  h3: "mt-3 mb-1.5 text-[13px] font-semibold leading-snug text-neutral-muted-fg first:mt-0",
};

const unsupportedHeadingClasses = "mb-2 last:mb-0";

const blockquoteClasses =
  "my-3 border-l-2 border-neutral-border pl-3 text-neutral-muted-fg [&_p]:mb-1.5 [&_p:last-child]:mb-0";

const codeHighlighter = createLowlight(common);

function getMarkdownCodeLanguage(className?: string): string | null {
  const language = className?.match(/(?:^|\s)language-([^\s]+)/)?.[1]?.toLowerCase();

  if (!language || !codeHighlighter.registered(language)) {
    return null;
  }

  return language;
}

function renderHighlightedCodeNode(node: HighlightedCodeNode, key: string): ReactNode {
  if (node.type === "text") {
    return node.value;
  }

  const children = node.children.map((child, index) =>
    renderHighlightedCodeNode(child, `${key}-${index}`)
  );
  const className = Array.isArray(node.properties.className)
    ? node.properties.className.join(" ")
    : undefined;

  if (node.tagName !== "span") {
    return <Fragment key={key}>{children}</Fragment>;
  }

  return (
    <span className={className} key={key}>
      {children}
    </span>
  );
}

function normalizeTableTextAlign(value: unknown): CSSProperties["textAlign"] | undefined {
  if (value !== "left" && value !== "center" && value !== "right") {
    return undefined;
  }

  return value;
}

function getTableCellStyle(align?: string, style?: CSSProperties): CSSProperties | undefined {
  const textAlign = normalizeTableTextAlign(align) ?? normalizeTableTextAlign(style?.textAlign);

  if (!textAlign) {
    return style;
  }

  return { ...style, textAlign };
}

export function MessageMarkdown({
  content,
  className,
  onNavigate,
  features,
}: MessageMarkdownProps) {
  const mentionInteraction = useOptionalMentionInteraction();
  const resolveReferencePresentation = useReferencePresentationResolver();
  const resolvedFeatures = resolveMessageFeatureFlags(features);
  // Only a Markdown link can become a chip. A reserved evidence path written as prose or
  // inside inline code is model-authored transport detail, so redact it before parsing —
  // this applies on every surface, including those with the evidence capability off.
  // A legacy `@Label [df-mention type=… id=…]` annotation is repaired the same way: the
  // backend canonicalises persisted text at settle, but the in-flight stream reaches this
  // renderer first, so this is the last line of defence against raw markers (LAT-895).
  const safeContent = redactVisibleDocumentEvidencePaths(
    canonicalizeMessageMentionAnnotations(content)
  );

  return (
    <div
      className={cn(
        "message-markdown",
        "leading-relaxed text-foreground",
        "[&_strong]:font-semibold",
        "[&_p]:mb-2 [&_p:last-child]:mb-0",
        "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:list-outside [&_ul]:pl-6",
        "[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:pl-6",
        "[&_li]:my-1",
        "[&_hr]:my-4 [&_hr]:border-border",
        "[&_a:not(.reference-chip)]:text-primary [&_a]:no-underline",
        "[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-2",
        "[&_code]:rounded-none [&_code]:bg-muted/70 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[13px]",
        "[&_td_code]:![font-size:inherit] [&_th_code]:![font-size:inherit]",
        "[&_pre_code]:bg-transparent [&_pre_code]:p-0",
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        // Adjacent citations are one marker, so the run is decided on the tree — the `a`
        // renderer sees one node per run and never has to reason about its neighbours.
        // Highlights run after: they only split text nodes, which leaves the citation runs
        // (whitespace, `br` and `a` nodes) exactly as the grouping pass found them.
        rehypePlugins={[rehypeDocumentEvidenceRuns, rehypeMessageHighlights, rehypeImagesAsLinks]}
        components={{
          h1: ({ children }) => <h1 className={headingClasses.h1}>{children}</h1>,
          h2: ({ children }) => <h2 className={headingClasses.h2}>{children}</h2>,
          h3: ({ children }) => <h3 className={headingClasses.h3}>{children}</h3>,
          h4: ({ children }) => <p className={unsupportedHeadingClasses}>{children}</p>,
          h5: ({ children }) => <p className={unsupportedHeadingClasses}>{children}</p>,
          h6: ({ children }) => <p className={unsupportedHeadingClasses}>{children}</p>,
          blockquote: ({ children }) => (
            <blockquote className={blockquoteClasses}>{children}</blockquote>
          ),
          table: ({ children }) => (
            <div
              className={cn("my-3", markdownTableWrapperClasses)}
              data-message-markdown-table-wrapper=""
            >
              <table className={markdownTableClasses}>{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className={markdownTableHeadClasses}>{children}</thead>,
          th: ({ align, children, style }) => (
            <th className={markdownTableHeaderCellClasses} style={getTableCellStyle(align, style)}>
              {children}
            </th>
          ),
          td: ({ align, children, style }) => (
            <td className={markdownTableCellClasses} style={getTableCellStyle(align, style)}>
              {children}
            </td>
          ),
          code: ({ children, className, ...props }) => {
            const language = getMarkdownCodeLanguage(className);

            if (!language) {
              return (
                <code {...props} className={className}>
                  {children}
                </code>
              );
            }

            const highlightedCode = codeHighlighter.highlight(
              language,
              flattenReactNodeText(children)
            );
            const highlightedChildren = highlightedCode.children as HighlightedCodeNode[];

            return (
              <code
                {...props}
                className={cn(className, "hljs")}
                data-message-markdown-code-language={language}
              >
                {highlightedChildren.map((child, index) =>
                  renderHighlightedCodeNode(child, String(index))
                )}
              </code>
            );
          },
          a: ({ href, children, node, ...props }) => {
            const resolvedHref = href ?? "";
            const evidenceHref = classifyDocumentEvidenceHref(resolvedHref);
            if (evidenceHref.kind !== "unrelated") {
              return renderDocumentEvidenceLink({
                children,
                enabled: resolvedFeatures.documentEvidence,
                href: resolvedHref,
                node,
              });
            }

            const mention = parseMessageMentionHref(resolvedHref);
            if (mention) {
              if (!resolvedFeatures.mentions) {
                const childText = flattenReactNodeText(children).trim();
                return (
                  <span>
                    {normalizeReferenceLabel(childText.length > 0 ? childText : `@${mention.id}`)}
                  </span>
                );
              }

              return renderMessageMentionLink({
                children,
                mention,
                mentionInteraction,
                resolveReferencePresentation,
              });
            }

            if (!resolvedFeatures.internalLinkResolution) {
              return (
                <a
                  {...props}
                  href={resolvedHref}
                  target={isExternalHref(resolvedHref) ? "_blank" : undefined}
                  rel={isExternalHref(resolvedHref) ? "noreferrer noopener" : undefined}
                >
                  {children}
                </a>
              );
            }

            const internalPath = toInternalAppPath(resolvedHref);
            const referencePath = getClaudiaReferenceInternalPath(resolvedHref);
            const linkLabel = internalPath ? getInternalAppLinkLabel(internalPath) : null;
            const childText = flattenReactNodeText(children).trim();
            const shouldSwapText =
              Boolean(linkLabel) &&
              childText.length > 0 &&
              (childText === resolvedHref || childText === internalPath);
            const label = shouldSwapText ? linkLabel : children;

            if (referencePath) {
              return (
                <ClaudiaReferenceLink {...props} href={resolvedHref} onNavigate={onNavigate}>
                  {label}
                </ClaudiaReferenceLink>
              );
            }

            return (
              <a
                {...props}
                href={resolvedHref}
                target={internalPath ? undefined : "_blank"}
                rel={internalPath ? undefined : "noreferrer noopener"}
                onClick={(event) => {
                  if (!internalPath || !onNavigate) return;
                  event.preventDefault();
                  onNavigate(internalPath);
                }}
              >
                {shouldSwapText ? linkLabel : children}
              </a>
            );
          },
        }}
      >
        {safeContent}
      </ReactMarkdown>
    </div>
  );
}
